/**
 * In-call style-feedback model (R14 B1 — the S9 end-scene interactive walkthrough).
 *
 * The end-scene reveal is no longer a passive "here's the read I got" — the visitor can now
 * confirm/correct it in-call. Gigi's captured facet chips arrive **pre-selected**; a small palette
 * per facet lets the visitor add ones she missed; tapping a captured chip deselects it. On Confirm
 * the kiosk sends `style-feedback {kept, removed, added}` (R31 §12) so the correction persists to
 * the lead + preference graph (curator's lift discipline — these are DECLARED prefs, the strongest
 * signal). Skippable: "looks right" / skip = confirm-as-is.
 *
 * Contract note (tolerant): R31's §12 extends §10.1's `style_snapshot` with an optional `palette`
 * (the additional selectable values per facet) and marks the snapshot interactive. Because the R31
 * server half may not have frozen the exact nesting yet, this module parses the palette TOLERANTLY
 * from BOTH plausible nestings (top-level `palette`, or nested under `snapshot`/`style_snapshot`/
 * `payload`) — the same seam discipline that fixed `ui.categories` and `ui.contact_field`. When no
 * palette arrives the walkthrough falls back to a static per-facet option set so the visitor can
 * always add more.
 *
 * Pure (no React / no DOM) so the {kept, removed, added} diff + palette parse are unit-testable.
 */

import type { StyleSnapshot } from './endScene';

/**
 * The facets the end-scene feedback covers (mirrors the StyleSnapshot facet arrays).
 * R15 T3 — `materials` added: §12.4's `style_snapshot` carries a `materials` facet (pre-selected +
 * palette) that the end scene now surfaces alongside style/finish/tone.
 */
export type FeedbackFacet = 'styles' | 'finishes' | 'materials' | 'colourTones';

export const FEEDBACK_FACETS: readonly FeedbackFacet[] = [
  'styles',
  'finishes',
  'materials',
  'colourTones',
];

/** Human labels for each facet (editorial, not the wire key). */
export const FACET_LABEL: Record<FeedbackFacet, string> = {
  styles: 'Style',
  finishes: 'Finish preference',
  materials: 'Material',
  colourTones: 'Colour tone',
};

/**
 * The palette of *additional* values the visitor can add per facet, beyond what Gigi captured.
 * Parsed from the server payload when present; else a sensible static fallback (aligned with the
 * style-snapshot walkthrough's option sets) so "add more" is never empty.
 */
export type FeedbackPalette = Record<FeedbackFacet, string[]>;

/** Static fallback palette (used when the server sends no `palette`). */
export const FALLBACK_PALETTE: FeedbackPalette = {
  styles: ['Contemporary', 'Coastal', 'Art Deco', 'Rustic', 'Minimalist', 'Eclectic'],
  finishes: ['Hammered', 'Brushed', 'Antique', 'Polished', 'Textured', 'Matte'],
  // R15 T3 — the material palette mirrors the catalogue's material vocabulary (brass/iron/aluminium/
  // marble/wood; §12.4). The server's palette (when present) is merged in ahead of this fallback.
  materials: ['Brass', 'Iron', 'Aluminium', 'Marble', 'Wood', 'Glass'],
  colourTones: [
    'Warm neutrals',
    'Gold tones',
    'Silver tones',
    'Dark earthy',
    'Light natural',
    'Mixed metals',
  ],
};

/**
 * The `style-feedback` client message payload (R31 §12): the diff between Gigi's captured read and
 * the visitor's confirmed selection.
 *   - kept:    captured values still selected (unchanged confirmation)
 *   - removed: captured values the visitor deselected (Gigi got this wrong)
 *   - added:   palette values the visitor added (Gigi missed these)
 * All three carry the human-readable value strings (the same tokens the snapshot uses).
 */
export type StyleFeedback = {
  kept: string[];
  removed: string[];
  added: string[];
};

/** Coerce an unknown into a clean, trimmed, de-duplicated string[] (bare string, array, or nothing). */
function toStringArray(v: unknown): string[] {
  const out: string[] = [];
  const push = (s: unknown) => {
    if (typeof s === 'string') {
      const t = s.trim();
      if (t !== '') out.push(t);
    }
  };
  if (Array.isArray(v)) for (const x of v) push(x);
  else push(v);
  return dedupe(out);
}

function dedupe(arr: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of arr) {
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

type RawPalette = {
  styles?: unknown;
  style?: unknown;
  finishes?: unknown;
  finish?: unknown;
  materials?: unknown;
  material?: unknown;
  colour_tones?: unknown;
  colourTones?: unknown;
  colour_tone?: unknown;
  colourTone?: unknown;
};

/**
 * Parse the additional-values palette from a `ui.scene {scene:'end'}` payload. Tolerant of nesting
 * (the palette may sit at the top of `payload`, or under `payload.palette`, `payload.snapshot`,
 * `payload.style_snapshot`) and of singular/plural + snake/camel facet keys. Merges the parsed
 * per-facet values with the static fallback (fallback first, then any server extras), always
 * excluding whatever the snapshot already captured (those are the pre-selected chips, not palette).
 * Always returns a full FeedbackPalette (never null) — "add more" is never empty.
 */
export function parseFeedbackPalette(payload: unknown, captured: StyleSnapshot): FeedbackPalette {
  const rawPalette = extractPaletteSource(payload);
  const src = (rawPalette ?? {}) as RawPalette;
  const serverStyles = [...toStringArray(src.styles), ...toStringArray(src.style)];
  const serverFinishes = [...toStringArray(src.finishes), ...toStringArray(src.finish)];
  const serverMaterials = [...toStringArray(src.materials), ...toStringArray(src.material)];
  const serverTones = [
    ...toStringArray(src.colour_tones),
    ...toStringArray(src.colourTones),
    ...toStringArray(src.colour_tone),
    ...toStringArray(src.colourTone),
  ];
  return {
    styles: mergePalette(FALLBACK_PALETTE.styles, serverStyles, captured.styles),
    finishes: mergePalette(FALLBACK_PALETTE.finishes, serverFinishes, captured.finishes),
    materials: mergePalette(FALLBACK_PALETTE.materials, serverMaterials, captured.materials),
    colourTones: mergePalette(FALLBACK_PALETTE.colourTones, serverTones, captured.colourTones),
  };
}

/** Find the palette-bearing object anywhere in the tolerated nestings, or null. */
function extractPaletteSource(payload: unknown): object | null {
  if (!payload || typeof payload !== 'object') return null;
  const outer = payload as {
    palette?: unknown;
    snapshot?: unknown;
    style_snapshot?: unknown;
    payload?: unknown;
  };
  // Prefer an explicit `palette` at any of the tolerated depths, else fall through to the snapshot
  // object itself (some emitters put the palette values alongside the captured facets).
  const candidates: unknown[] = [
    outer.palette,
    isObj(outer.snapshot) && (outer.snapshot as { palette?: unknown }).palette,
    isObj(outer.style_snapshot) && (outer.style_snapshot as { palette?: unknown }).palette,
    isObj(outer.payload) && (outer.payload as { palette?: unknown }).palette,
    outer.snapshot,
    outer.style_snapshot,
    outer.payload,
    outer,
  ];
  for (const c of candidates) if (isObj(c)) return c as object;
  return null;
}

function isObj(v: unknown): v is object {
  return !!v && typeof v === 'object';
}

/** fallback ∪ server extras, minus already-captured values (case-insensitive), order-stable. */
function mergePalette(fallback: string[], serverExtras: string[], captured: string[]): string[] {
  const capturedLower = new Set(captured.map((c) => c.toLowerCase()));
  return dedupe([...fallback, ...serverExtras]).filter((v) => !capturedLower.has(v.toLowerCase()));
}

/**
 * The selection state the interactive end scene edits: the currently-selected values per facet,
 * seeded from the captured snapshot (all captured = pre-selected). Toggling adds/removes a value.
 */
export type FeedbackSelection = Record<FeedbackFacet, string[]>;

/** Seed the editable selection from Gigi's captured read (every captured facet value pre-selected). */
export function initialSelection(captured: StyleSnapshot): FeedbackSelection {
  return {
    styles: dedupe(captured.styles),
    finishes: dedupe(captured.finishes),
    materials: dedupe(captured.materials),
    colourTones: dedupe(captured.colourTones),
  };
}

/**
 * R26 U1 — seed the editable selection from BOTH the legacy pre-selected read (`captured`) AND the
 * §36 `chips` pre-ticks (`parseChipPreTicks(payload)`), unioned per facet (case-insensitive, legacy
 * casing wins on a clash). This is the R26 render truth: a `chips`-only pre-tick — a verbally-declared
 * value the legacy list doesn't carry (the fixture's spoken "Brass") — MUST show selected (§36.1).
 * When there is no `chips` map the legacy seed stands unchanged (older/absent emit → pre-R26 seed).
 * Pure. (`parseChipPreTicks`/`extractChips` are defined below — hoisted function declarations.)
 */
export function initialSelectionFromPayload(
  captured: StyleSnapshot,
  payload: unknown
): FeedbackSelection {
  const base = initialSelection(captured);
  const chipTicks = parseChipPreTicks(payload);
  if (!chipTicks) return base;
  const out: FeedbackSelection = { styles: [], finishes: [], materials: [], colourTones: [] };
  for (const facet of FEEDBACK_FACETS) {
    const seen = new Set(base[facet].map((v) => v.toLowerCase()));
    const merged = [...base[facet]];
    for (const v of chipTicks[facet]) {
      if (!seen.has(v.toLowerCase())) {
        seen.add(v.toLowerCase());
        merged.push(v);
      }
    }
    out[facet] = merged;
  }
  return out;
}

/** Toggle a value in a facet's selection (immutable — returns a new selection). */
export function toggleSelection(
  sel: FeedbackSelection,
  facet: FeedbackFacet,
  value: string
): FeedbackSelection {
  const cur = sel[facet];
  const has = cur.some((v) => v.toLowerCase() === value.toLowerCase());
  return {
    ...sel,
    [facet]: has ? cur.filter((v) => v.toLowerCase() !== value.toLowerCase()) : [...cur, value],
  };
}

/**
 * Compute the `style-feedback {kept, removed, added}` diff from the captured read + the visitor's
 * confirmed selection across all facets. Values are compared case-insensitively; the ORIGINAL-cased
 * value (captured for kept/removed, selected for added) is carried in the result.
 *   - kept:    captured ∩ selected
 *   - removed: captured − selected
 *   - added:   selected − captured
 */
export function computeFeedback(
  captured: StyleSnapshot,
  selection: FeedbackSelection
): StyleFeedback {
  const capturedAll: string[] = [
    ...captured.styles,
    ...captured.finishes,
    ...captured.materials,
    ...captured.colourTones,
  ];
  const selectedAll: string[] = [
    ...selection.styles,
    ...selection.finishes,
    ...selection.materials,
    ...selection.colourTones,
  ];
  const selectedLower = new Set(selectedAll.map((v) => v.toLowerCase()));
  const capturedLower = new Set(capturedAll.map((v) => v.toLowerCase()));
  const kept = dedupe(capturedAll.filter((v) => selectedLower.has(v.toLowerCase())));
  const removed = dedupe(capturedAll.filter((v) => !selectedLower.has(v.toLowerCase())));
  const added = dedupe(selectedAll.filter((v) => !capturedLower.has(v.toLowerCase())));
  return { kept, removed, added };
}

/**
 * R17 Q3 (R34 §16) — flatten the current per-facet selection into the flat, de-duplicated list of
 * selected chip-value tokens for the `style-chips-updated {selected:[…]}` context message. Facet
 * order is stable (styles → finishes → materials → colourTones) so a re-emit of an unchanged
 * selection produces a byte-identical payload (the emitter can dedupe re-sends). Pure.
 */
export function flattenSelection(selection: FeedbackSelection): string[] {
  return dedupe([
    ...selection.styles,
    ...selection.finishes,
    ...selection.materials,
    ...selection.colourTones,
  ]);
}

// ── R26 U1 — verbal-declared pre-ticks (voice-capture-contract.md §36, FROZEN) ─
/**
 * R26 U1 — per-facet set of pre-selected values the visitor **said aloud** (`declared:verbal`,
 * routed through the agent's `note_style_preference` tool, R44 / declared-prefs-contract.md §"Two
 * provenances"). These render as pre-ticked chips with a distinct "you mentioned" affordance so a
 * spoken declaration reads back visibly, separate from Gigi's inferred/tapped read. The grounding
 * law holds: ONLY explicitly-declared values carry this flag — an inferred-only pref (product picks,
 * search analysis / browse cue → `source:'inferred'`) is a plain pre-selected chip, never a "you
 * mentioned" one.
 *
 * WIRE (voice-capture-contract.md §36, FROZEN 2026-07-18 — captured fixture
 * `apps/agent-worker/tests/fixtures/r49/style-snapshot-verbal-preticks.json`, mirrored into
 * `r26-verbal-preticks.test.ts`): provenance rides **PER-CHIP** under `style_snapshot.chips`:
 *
 *   payload.style_snapshot.chips[<facet>] = [{ value, preTicked?, source? }]
 *
 * where `source:'verbal'` = persisted `declared:verbal` (said aloud) and `source:'inferred'` =
 * browse/cue pre-selection. Facet keys are snake_case plural: `finishes` / `materials` /
 * `colour_tones` / `styles`. The legacy per-facet arrays + `palette` are unchanged (endScene.ts +
 * parseFeedbackPalette still own the pre-selected set + the add-more palette); this reader ONLY
 * extracts the verbal provenance for the render flag. Bound to the captured §36 fixture — there is
 * NO `verbal`/`sources` sub-map (the R26-authoring speculative shapes were dropped once §36 froze).
 */
export type VerbalDeclared = Record<FeedbackFacet, string[]>;

const EMPTY_VERBAL: VerbalDeclared = { styles: [], finishes: [], materials: [], colourTones: [] };

/**
 * The §36 wire facet key per FeedbackFacet (snake_case plural, first entry = the frozen fixture's
 * key), plus tolerated aliases (snake/camel, singular) for the emission-shape≠subscription-shape
 * seam that bit `ui.categories`/`ui.scene` before.
 */
const CHIP_FACET_KEYS: Record<FeedbackFacet, readonly string[]> = {
  finishes: ['finishes', 'finish'],
  materials: ['materials', 'material'],
  colourTones: ['colour_tones', 'colourTones', 'colour_tone', 'colourTone'],
  styles: ['styles', 'style'],
};

/** A single §36 chip descriptor: `{ value, preTicked?, source? }`. */
type RawChip = { value?: unknown; preTicked?: unknown; source?: unknown };

/** Find `style_snapshot.chips` across the tolerated nestings (top-level / `payload` / `snapshot`). */
function extractChips(payload: unknown): Record<string, unknown> | null {
  if (!isObj(payload)) return null;
  const outer = payload as Record<string, unknown>;
  // Step into the same wrappers endScene.ts tolerates: payload / style_snapshot / snapshot.
  const scopes: unknown[] = [
    outer,
    outer.payload,
    outer.style_snapshot,
    outer.snapshot,
    isObj(outer.payload) && (outer.payload as Record<string, unknown>).style_snapshot,
    isObj(outer.payload) && (outer.payload as Record<string, unknown>).snapshot,
  ];
  for (const s of scopes) {
    if (isObj(s)) {
      const chips = (s as Record<string, unknown>).chips;
      if (isObj(chips)) return chips as Record<string, unknown>;
    }
  }
  return null;
}

/** Iterate the valid `{value,…}` chip descriptors of a facet across its key aliases. */
function chipsForFacet(chips: Record<string, unknown>, facet: FeedbackFacet): RawChip[] {
  const out: RawChip[] = [];
  for (const key of CHIP_FACET_KEYS[facet]) {
    const arr = chips[key];
    if (!Array.isArray(arr)) continue;
    for (const c of arr) {
      if (isObj(c) && typeof (c as RawChip).value === 'string') out.push(c as RawChip);
    }
  }
  return out;
}

/**
 * Parse the per-facet **pre-ticked** set from a §36 `chips` payload — every chip flagged
 * `preTicked:true` renders SELECTED (§36.1). This is the R26 render truth: a verbally-declared value
 * the legacy pre-selected list doesn't carry (e.g. a spoken "Brass" not in the inferred `materials`
 * array) is unioned into `chips` and MUST pre-tick. `parseEndScenePayload`'s legacy arrays seed the
 * baseline; this augments it so `chips`-only pre-ticks (verbal or inferred) still show selected.
 * Returns null when there's no `chips` map (older/absent emit) so the caller keeps the legacy seed.
 */
export function parseChipPreTicks(payload: unknown): FeedbackSelection | null {
  const chips = extractChips(payload);
  if (!chips) return null;
  const out: FeedbackSelection = { styles: [], finishes: [], materials: [], colourTones: [] };
  for (const facet of FEEDBACK_FACETS) {
    const vals: string[] = [];
    for (const chip of chipsForFacet(chips, facet)) {
      if (chip.preTicked === true) {
        const v = (chip.value as string).trim();
        if (v !== '') vals.push(v);
      }
    }
    out[facet] = dedupe(vals);
  }
  return out;
}

/**
 * Parse the per-facet verbally-declared set from a §36 `chips` payload — the chips flagged
 * `source:'verbal'` (a persisted `declared:verbal`, said aloud). These get the "you mentioned"
 * affordance. `source:'inferred'` (browse/cue) is NOT verbal (grounding law). Always returns a full
 * VerbalDeclared (never null); absent/malformed `chips` → all facets empty (every pre-tick renders
 * plain — the graceful pre-R26 fallback). Case-insensitive; carries the chip's own casing.
 *
 * The verbal set reads the chips' OWN `preTicked`+`source` (`chips` is the render truth per §36.1),
 * NOT the legacy `parseEndScenePayload` arrays — a verbal value can legitimately be absent from the
 * legacy pre-selected list yet pre-ticked via `chips` (the fixture's "Brass"). A verbal chip that is
 * somehow NOT pre-ticked is ignored (never flag an un-ticked chip). Pairs with
 * `initialSelectionFromPayload`, which unions those pre-ticks into the seeded selection.
 */
export function parseVerbalDeclared(payload: unknown): VerbalDeclared {
  const chips = extractChips(payload);
  if (!chips) return { ...EMPTY_VERBAL };
  const out: VerbalDeclared = { styles: [], finishes: [], materials: [], colourTones: [] };
  for (const facet of FEEDBACK_FACETS) {
    const vals: string[] = [];
    for (const chip of chipsForFacet(chips, facet)) {
      if (chip.preTicked === true && chip.source === 'verbal') {
        const v = (chip.value as string).trim();
        if (v !== '') vals.push(v);
      }
    }
    out[facet] = dedupe(vals);
  }
  return out;
}

/** True iff `value` (case-insensitive) is in this facet's verbally-declared set. */
export function isVerbalDeclared(
  verbal: VerbalDeclared,
  facet: FeedbackFacet,
  value: string
): boolean {
  return verbal[facet].some((v) => v.toLowerCase() === value.toLowerCase());
}
